import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useView } from '../context/ViewContext';
import { useOrganisme } from '../context/OrganismeContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  HomeIcon,
  UsersIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  NewspaperIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  ArrowsRightLeftIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  DocumentChartBarIcon,
  BanknotesIcon,
  Bars3Icon,
  XMarkIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { switchView, hasMultipleViews } = useView();
  const { organismeActif, isSuperAdminMode, switchOrganisme, enableSuperAdminMode, disableSuperAdminMode, hasMultipleOrganismes, isSuperAdmin, organismes } = useOrganisme();
  const { canAccessCommunications, canAccessBlog, canAccessUsers, canAccessUnafServices, canAccessEvents, canAccessMiellerie, permissions } = usePermissions();
  const canAccessComposition = user?.role === 'super_admin' || permissions?.gestionActivite?.modifierInfosStatutaires;
  const canAccessReunions = user?.role === 'super_admin' || permissions?.reunions?.access;
  const canAccessFinances = user?.role === 'super_admin' || permissions?.finances?.access;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingVolunteers, setPendingVolunteers] = useState(0);
  const [canManageMiellerie, setCanManageMiellerie] = useState(false);

  useEffect(() => {
    if (!user || isSuperAdminMode) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${process.env.REACT_APP_API_URL}/events/pending-volunteers-count`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(r => setPendingVolunteers(r.data.count || 0))
      .catch(() => {});
  }, [user, isSuperAdminMode]);

  useEffect(() => {
    if (!user) {
      setCanManageMiellerie(false);
      return;
    }
    const roles = user.roles || [];
    const hasAMAIR = (user.organismes || []).includes('AMAIR');
    if (roles.includes('super_admin') || (canAccessMiellerie() && hasAMAIR)) {
      setCanManageMiellerie(true);
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios
      .get(`${process.env.REACT_APP_API_URL}/miellerie/responsables/mon-statut`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { annee: new Date().getFullYear() },
      })
      .then((r) => {
        if (!cancelled) setCanManageMiellerie(Boolean(r.data?.accesGestion));
      })
      .catch(() => {
        if (!cancelled) setCanManageMiellerie(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, permissions]);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };
  
  const isSuperAdminRole = user?.role === 'super_admin' || (user?.roles || []).includes('super_admin');
  
  // Fonction pour switcher vers l'interface user
  const handleSwitchToUser = () => {
    switchView('user');
    navigate('/dashboard');
  };

  // Couleurs selon le rôle
  const colors = {
    bg: isSuperAdminRole ? 'bg-gray-900' : 'bg-gray-900',
    bgHeader: isSuperAdminRole ? 'bg-black' : 'bg-gray-800',
    bgHover: isSuperAdminRole ? 'bg-gray-900' : 'bg-gray-800',
    bgActive: isSuperAdminRole ? 'bg-gray-900' : 'bg-gray-800',
    border: isSuperAdminRole ? 'border-gray-900' : 'border-gray-800',
    iconBg: isSuperAdminRole ? 'bg-yellow-500' : 'bg-indigo-600',
    userBg: isSuperAdminRole ? 'bg-yellow-500' : 'bg-indigo-600',
  };

  // Navigation filtrée selon les permissions et le mode
  const navigation = [
    { name: 'Tableau de bord', href: '/admin/dashboard', icon: HomeIcon, current: location.pathname === '/admin/dashboard', show: !isSuperAdminMode && true },
    { name: 'Adhésions', href: '/admin/users', icon: UsersIcon, current: location.pathname === '/admin/users', show: !isSuperAdminMode && canAccessUsers() },
    // { name: 'Régularisations', href: '/admin/regularisations', icon: DocumentTextIcon, current: location.pathname === '/admin/regularisations', show: !isSuperAdminMode && canAccessAdherents() },
    { name: 'Paiements', href: '/admin/payments', icon: CreditCardIcon, current: location.pathname === '/admin/payments', show: !isSuperAdminMode && true },
    { name: 'Suivi services de l\'UNAF', href: '/admin/suivi-assurance', icon: DocumentChartBarIcon, current: location.pathname === '/admin/suivi-assurance', show: !isSuperAdminMode && canAccessUnafServices() && organismeActif === 'SAR' },
    { name: 'Gestion miellerie', href: '/gestion-miellerie', icon: ClipboardDocumentListIcon, current: location.pathname === '/gestion-miellerie', show: !isSuperAdminMode && organismeActif === 'AMAIR' && canManageMiellerie },
    { name: 'Suivi des réunions', href: '/admin/reunions', icon: CalendarDaysIcon, current: location.pathname === '/admin/reunions', show: !isSuperAdminMode && canAccessReunions },
    { name: 'Composition Conseil', href: '/admin/composition-conseil', icon: BuildingLibraryIcon, current: location.pathname === '/admin/composition-conseil', show: !isSuperAdminMode && canAccessComposition },
  ].filter(item => item.show);

  const superAdminNavigation = [
    { name: 'Organismes', href: '/super-admin/organismes', icon: BuildingOffice2Icon, current: location.pathname === '/super-admin/organismes', show: isSuperAdminMode && isSuperAdminRole },
    { name: 'Administrateurs', href: '/super-admin/admins', icon: UserGroupIcon, current: location.pathname === '/super-admin/admins', show: isSuperAdminMode && isSuperAdminRole },
    { name: 'Tous les utilisateurs', href: '/super-admin/all-users', icon: UsersIcon, current: location.pathname === '/super-admin/all-users', show: isSuperAdminMode && isSuperAdminRole },
    { name: 'Documents statutaires', href: '/super-admin/campagne-documents', icon: DocumentTextIcon, current: location.pathname === '/super-admin/campagne-documents', show: isSuperAdminMode && isSuperAdminRole },
    { name: 'Paramètres adhésion', href: '/admin/parametres-adhesion', icon: Cog6ToothIcon, current: location.pathname === '/admin/parametres-adhesion', show: isSuperAdminMode && isSuperAdminRole },
    { name: 'Compte Stripe', href: '/admin/stripe-account', icon: BanknotesIcon, current: location.pathname === '/admin/stripe-account', show: isSuperAdminMode && canAccessFinances },
    { name: 'Import adhésions', href: '/super-admin/import-adhesions', icon: ArrowUpTrayIcon, current: location.pathname === '/super-admin/import-adhesions', show: isSuperAdminMode && isSuperAdminRole },
    { name: 'Purges', href: '/super-admin/purges', icon: TrashIcon, current: location.pathname === '/super-admin/purges', show: isSuperAdminMode && isSuperAdminRole && process.env.NODE_ENV !== 'production' },
    // { name: 'Outils Dev (PDF)', href: '/super-admin/dev-tools', icon: WrenchScrewdriverIcon, current: location.pathname === '/super-admin/dev-tools', show: isSuperAdminMode && isSuperAdminRole },
  ].filter(item => item.show);

  const showMiellerieHorsAmair =
    canManageMiellerie && !(user?.organismes || []).includes('AMAIR');
  const showSectionTousOrganismes =
    canAccessEvents() || canAccessBlog() || canAccessCommunications() || showMiellerieHorsAmair;

  const secondaryNavigation = [
    { name: 'Sécurité', href: '/securite', icon: Cog6ToothIcon, show: true },
  ].filter(item => item.show);

  const SidebarContent = () => (
    <div className={`flex flex-col h-full ${colors.bg} w-64`}>
      {/* Logo et nom */}
      <div className={`flex items-center h-16 px-6 ${colors.bgHeader}`}>
        <div className="flex items-center space-x-4">
          <img src="/icon.png" alt="Logo Apiculture" className="w-auto h-10" />
          <div>
            <div className="text-white text-sm">Abeille Réunion</div>
            <div className="text-white font-semibold text-sm">
              {isSuperAdminMode ? 'Super Admin' : organismeActif || 'Admin'}
            </div>
          </div>
        </div>
      </div>

      {/* Sélecteur d'organisme / Mode Super Admin */}
      {(user?.role === 'admin' || user?.role === 'super_admin') && (
        <div className="px-3 pt-3">
          {/* Mode Super Admin Toggle (pour super admins uniquement) */}
          {isSuperAdmin() && (
            <div className="mb-3">
              <button
                onClick={() => isSuperAdminMode ? disableSuperAdminMode() : enableSuperAdminMode()}
                className={`w-full px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isSuperAdminMode
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{isSuperAdminMode ? '🔧 Mode Super Admin' : '🏢 Mode Admin Organisme'}</span>
                  <ShieldCheckIcon className="h-4 w-4" />
                </div>
              </button>
            </div>
          )}

          {/* Sélecteur d'organisme (visible uniquement en mode admin organisme) */}
          {!isSuperAdminMode && organismes.length > 0 && (
            <div className="border-t border-gray-800 px-3">
              {hasMultipleOrganismes() ? (
                <div className="mt-2">
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                    Organisme actif 
                  </label>
                  <select
                    value={organismeActif || ''}
                    onChange={(e) => switchOrganisme(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-gray-800 text-white border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 mt-1"
                  >
                    {organismes.map((org) => (
                      <option key={org} value={org}>
                        {org}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="px-3 py-2 text-sm bg-gray-800 text-white rounded-md text-center">
                  {organismeActif}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Navigation principale */}
      <nav className="flex-1 px-3 pt-2 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const Icon = item.icon;
          const showBadge = item.href === '/admin/events' && pendingVolunteers > 0;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={handleNavClick}
              className={`
                flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                ${item.current
                  ? `${colors.bgActive} text-white`
                  : `text-gray-300 hover:${colors.bgHover} hover:text-white`
                }
              `}
            >
              <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
              <span className="flex-1">{item.name}</span>
              {showBadge && (
                <span className="ml-auto flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900">
                  {pendingVolunteers}
                </span>
              )}
            </Link>
          );
        })}

        {/* ── Section multi-organismes ────────────────────────────────── */}
        {!isSuperAdminMode && showSectionTousOrganismes && (
          <div className="pt-3 mt-2 border-t border-gray-700/60">
            <div className="flex items-center gap-1.5 px-3 mb-1 mt-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                Tous les organismes
              </span>
            </div>

            {/* Évènements */}
            {canAccessEvents() && (
              <Link
                to="/admin/events"
                onClick={handleNavClick}
                className={`
                  flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                  ${location.pathname.startsWith('/admin/events')
                    ? `${colors.bgActive} text-white`
                    : `text-gray-300 hover:${colors.bgHover} hover:text-white`
                  }
                `}
              >
                <CalendarDaysIcon className="mr-3 h-5 w-5 flex-shrink-0" />
                <span className="flex-1">Évènements</span>
                {pendingVolunteers > 0 && (
                  <span className="ml-auto flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900">
                    {pendingVolunteers}
                  </span>
                )}
              </Link>
            )}

            {showMiellerieHorsAmair && (
              <Link
                to="/gestion-miellerie"
                onClick={handleNavClick}
                className={`
                  flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                  ${location.pathname === '/gestion-miellerie'
                    ? `${colors.bgActive} text-white`
                    : `text-gray-300 hover:${colors.bgHover} hover:text-white`
                  }
                `}
              >
                <ClipboardDocumentListIcon className="mr-3 h-5 w-5 flex-shrink-0" />
                Miellerie AMAIR
              </Link>
            )}

            {/* Actualités */}
            {canAccessBlog() && (
              <Link
                to="/admin/actualites"
                onClick={handleNavClick}
                className={`
                  flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                  ${location.pathname.startsWith('/admin/actualites')
                    ? `${colors.bgActive} text-white`
                    : `text-gray-300 hover:${colors.bgHover} hover:text-white`
                  }
                `}
              >
                <NewspaperIcon className="mr-3 h-5 w-5 flex-shrink-0" />
                Actualités
              </Link>
            )}

            {/* Communications */}
            {canAccessCommunications() && (
              <Link
                to="/admin/communications"
                onClick={handleNavClick}
                className={`
                  flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                  ${location.pathname.startsWith('/admin/communications')
                    ? `${colors.bgActive} text-white`
                    : `text-gray-300 hover:${colors.bgHover} hover:text-white`
                  }
                `}
              >
                <EnvelopeIcon className="mr-3 h-5 w-5 flex-shrink-0" />
                Communications
              </Link>
            )}
          </div>
        )}

        {/* Menu Super Admin niveau 1 (uniquement en mode Super Admin) */}
        {superAdminNavigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={handleNavClick}
              className={`
                flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                ${item.current
                  ? `${colors.bgActive} text-white`
                  : `text-gray-300 hover:${colors.bgHover} hover:text-white`
                }
              `}
            >
              <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Navigation secondaire */}
      <div className={`px-3 py-4 border-t ${colors.border}`}>
        
        {secondaryNavigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center px-3 py-2 text-sm font-medium text-gray-300 rounded-md hover:${colors.bgHover} hover:text-white transition-colors`}
            >
              <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
              {item.name}
            </Link>
          );
        })}
        
        <button
          onClick={logout}
          className={`w-full flex items-center px-3 py-2 mt-1 text-sm font-medium text-gray-300 rounded-md hover:${colors.bgHover} hover:text-white transition-colors`}
        >
          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 flex-shrink-0" />
          Déconnexion
        </button>
      </div>

      {/* User info */}
      <div className={`px-3 py-4 ${colors.bgHeader}`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`w-8 h-8 ${colors.userBg} rounded-full flex items-center justify-center`}>
              <span className="text-white text-sm font-medium">
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </span>
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-white">
              {user?.prenom} {user?.nom}
            </p>
            <p className="text-xs text-gray-400">
              {user?.role === 'super_admin' ? 'Super Administrateur' : 'Pilote de la plateforme'}
            </p>
          </div>
        </div>
        {/* Bouton de switch vers l'interface user (si l'admin a aussi le rôle user) */}
        {hasMultipleViews() && (
          <button
            onClick={handleSwitchToUser}
            className={`w-full flex items-center px-3 py-2 mt-4 mb-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors`}
          >
            <ArrowsRightLeftIcon className="mr-3 h-5 w-5 flex-shrink-0" />
            Espace adhérent
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-40 p-2 bg-gray-800 rounded-lg shadow-md"
      >
        <Bars3Icon className="h-6 w-6 text-white" />
      </button>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <SidebarContent />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Sidebar */}
          <div className="fixed inset-y-0 left-0 w-64 shadow-xl">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 z-10 p-1 text-gray-300 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
}
